import { zodResolver } from '@hookform/resolvers/zod'
import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'

import { Form } from 'src/shared/components/ui/forms/form'
import { cn } from 'src/shared/utils/cn'

import BusinessFormDisclaimer from '../../components/business-form-disclaimer'
import BusinessFormErrorMessage from '../../components/business-form-error-message'
import BusinessFormTitle from '../../components/business-form-title'
import { useBusinessCreateContext } from '../../context/business-create-context'
import { useBusinessCreateStore } from '../../store/business-create-store'

const ClosedDateSchema = z.object({
  closedAt: z
    .string()
    .min(1, { message: 'Closed date is required' })
    .refine((value) => {
      const selectedDate = new Date(`${value}T00:00:00`)
      if (Number.isNaN(selectedDate.getTime())) return false

      const today = new Date()
      today.setHours(23, 59, 59, 999)

      return selectedDate <= today
    }, 'Closed date must be today or earlier'),
})
type ClosedDateSchemaType = z.infer<typeof ClosedDateSchema>

const toDateInputValue = (value: string | null | undefined) => {
  if (!value) return ''
  const parsedDate = new Date(value)
  if (Number.isNaN(parsedDate.getTime())) return ''

  const year = parsedDate.getFullYear()
  const month = String(parsedDate.getMonth() + 1).padStart(2, '0')
  const day = String(parsedDate.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

const getTodayDateInputValue = () => {
  const today = new Date()
  const year = today.getFullYear()
  const month = String(today.getMonth() + 1).padStart(2, '0')
  const day = String(today.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

export default function BusinessCreateClosedDateStep() {
  const { setFormData, formData } = useBusinessCreateStore()
  const { handleNext, setFooterConfig } = useBusinessCreateContext()

  const form = useForm<ClosedDateSchemaType>({
    resolver: zodResolver(ClosedDateSchema),
    defaultValues: {
      closedAt: toDateInputValue(formData.closedAt),
    },
  })

  useEffect(() => {
    setFooterConfig(null)
  }, [setFooterConfig])

  const onSubmit = (data: ClosedDateSchemaType) => {
    setFormData({ ...formData, isClosed: true, closedAt: data.closedAt })
    handleNext()
  }

  return (
    <div className="flex size-full flex-col items-center justify-center gap-10">
      <BusinessFormTitle>{`When did this deal close?`}</BusinessFormTitle>
      <Form {...form}>
        <form id="business-create-form" onSubmit={form.handleSubmit(onSubmit)} className="w-full max-w-[420px]">
          <div className="relative flex w-full flex-col items-center justify-center gap-3">
            <input
              type="date"
              max={getTodayDateInputValue()}
              autoComplete="off"
              {...form.register('closedAt')}
              className={cn(
                'w-full rounded-full p-[10px] text-center text-[32px] font-bold tracking-[-0.6px] text-primary focus-visible:outline-none md:text-[42px]',
                !form.watch('closedAt') && 'text-[#D8D8D6]',
              )}
            />
            {form.formState.errors.closedAt && <BusinessFormErrorMessage error={form.formState.errors.closedAt} />}
          </div>
          <div className="mt-4 flex w-full items-center justify-center">
            <BusinessFormDisclaimer>Use the final signed closing date.</BusinessFormDisclaimer>
          </div>
        </form>
      </Form>
    </div>
  )
}
